import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import { EngineeringChart } from "@/components/engineering-insights/EngineeringChart";
import { type EngineeringInsightsGranularity } from "@/lib/engineering-insights/api";
import { type SquadMode } from "@/lib/engineering-insights/chart-data";
import type { EngineeringChartSpec } from "@/lib/engineering-insights/chart-specs";
import { type RangePreset } from "@/lib/engineering-insights/date-range";
import { slideLayout } from "@/lib/engineering-insights/presentation";
import { cn } from "@/lib/utils";

// Compressed variants of the page-toolbar filters (deliberately no custom
// date range in here); they drive the same page state, so changes persist
// after the presentation ends.
const OVERLAY_PRESETS: Array<{ id: Exclude<RangePreset, "custom">; label: string }> = [
  { id: "1m", label: "1M" },
  { id: "3m", label: "3M" },
  { id: "6m", label: "6M" },
  { id: "12m", label: "12M" },
  { id: "ytd", label: "YTD" },
];
const OVERLAY_GRANULARITIES: Array<{
  value: EngineeringInsightsGranularity;
  label: string;
}> = [
  { value: "week", label: "W" },
  { value: "month", label: "M" },
];
const OVERLAY_SQUAD_OPTIONS: Array<{ value: SquadMode; label: string }> = [
  { value: "all", label: "All squads" },
  { value: "compare", label: "Member vs B2B" },
  { value: "member", label: "Member" },
  { value: "b2b", label: "B2B" },
];

// Slot placement per layout (indexes into the slide's card list). Mirrors
// the schematic tiles in ArrangeDeckModal: for "stacked-left", cards are
// left-top, left-bottom, then the full-height right hero.
const STACKED_LEFT_SLOTS = [
  "col-start-1 row-start-1",
  "col-start-1 row-start-2",
  "col-start-2 row-start-1 row-span-2",
];

// Meeting Mode: a full-viewport in-app deck over the dashboard. Deliberately
// NOT the browser Fullscreen API — ⌃⌘F composes with this overlay for free,
// with none of the Esc-ordering conflicts. z-30 keeps it *below* the chart
// context menu (z-40/50) and RelatedDocumentsModal (z-50) so drill-down from
// a chart point stacks above the deck.

// "Right"/"Left"/"Spacebar" are legacy KeyboardEvent.key names still emitted
// by some presentation clickers — cheap robustness for the exact hardware
// this mode is used with.
const NAV_NEXT_KEYS = new Set(["ArrowRight", "Right", "PageDown", " ", "Spacebar"]);
const NAV_PREV_KEYS = new Set(["ArrowLeft", "Left", "PageUp"]);

export function MeetingModeOverlay({
  deck,
  from,
  to,
  squadMode,
  granularity,
  rangePreset,
  onRangePresetChange,
  onGranularityChange,
  onSquadModeChange,
  onClose,
}: {
  /** Ordered slides of 1–3 chart specs; layout derives from the count. */
  deck: EngineeringChartSpec[][];
  from: string;
  to: string;
  squadMode: SquadMode;
  granularity: EngineeringInsightsGranularity;
  rangePreset: RangePreset;
  onRangePresetChange: (preset: Exclude<RangePreset, "custom">) => void;
  onGranularityChange: (granularity: EngineeringInsightsGranularity) => void;
  onSquadModeChange: (mode: SquadMode) => void;
  onClose: () => void;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  // The deck is fixed for the overlay's lifetime (curation and filters live
  // on the page underneath), but clamp defensively.
  const safeIndex = Math.min(index, Math.max(deck.length - 1, 0));
  const slide = deck[safeIndex];

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    overlayRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore deck keys while a portaled dialog (RelatedDocumentsModal) is
      // open: focus is trapped inside it, so its role="dialog" ancestor is
      // not this overlay.
      const target = event.target as HTMLElement | null;
      const dialog = target?.closest?.('[role="dialog"]');
      if (dialog && dialog !== overlayRef.current) {
        return;
      }
      // The header filters include a native <select>: arrow keys there pick
      // options, not slides.
      const tag = target?.tagName;
      if (tag === "SELECT" || tag === "INPUT" || tag === "TEXTAREA") {
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (NAV_NEXT_KEYS.has(event.key)) {
        event.preventDefault();
        setIndex((current) => Math.min(current + 1, deck.length - 1));
        return;
      }
      if (NAV_PREV_KEYS.has(event.key)) {
        event.preventDefault();
        setIndex((current) => Math.max(current - 1, 0));
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [deck.length, onClose]);

  if (!slide || slide.length === 0) {
    return null;
  }
  const layout = slideLayout(slide.length);

  const atStart = safeIndex === 0;
  const atEnd = safeIndex === deck.length - 1;

  const navButtonClass = (disabled: boolean) =>
    cn(
      "grid h-11 w-11 shrink-0 place-items-center rounded-full border border-soft bg-surface text-anchor shadow-card transition-colors ring-focus",
      disabled ? "opacity-30" : "hover:bg-muted",
    );

  return (
    <div
      ref={overlayRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label="Meeting Mode presentation"
      className="fixed inset-0 z-30 flex flex-col gap-3 bg-[var(--background)] p-4 outline-none md:p-6"
    >
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--primary)]">
          Meeting Mode
        </div>
        <div className="flex items-center gap-1">
          {OVERLAY_PRESETS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={(event) => {
                onRangePresetChange(option.id);
                // Blur so the presenter's next Space/arrow flips slides
                // instead of re-triggering the pill.
                event.currentTarget.blur();
              }}
              className={cn(
                "h-7 rounded-[8px] px-2 text-[11px] font-medium transition-colors ring-focus",
                rangePreset === option.id
                  ? "bg-[var(--primary)] text-[color:var(--primary-foreground)]"
                  : "border border-soft text-anchor hover:bg-muted",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {OVERLAY_GRANULARITIES.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-label={option.value === "week" ? "Weekly" : "Monthly"}
              onClick={(event) => {
                onGranularityChange(option.value);
                event.currentTarget.blur();
              }}
              className={cn(
                "h-7 w-7 rounded-[8px] text-[11px] font-medium transition-colors ring-focus",
                granularity === option.value
                  ? "bg-[var(--primary)] text-[color:var(--primary-foreground)]"
                  : "border border-soft text-anchor hover:bg-muted",
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
        <label className="relative flex h-7 items-center rounded-[8px] border border-soft bg-surface pl-2 pr-6 transition-colors hover:bg-muted">
          <select
            value={squadMode}
            onChange={(event) => onSquadModeChange(event.target.value as SquadMode)}
            aria-label="Squad filter"
            className="appearance-none cursor-pointer bg-transparent text-[11px] font-medium text-anchor outline-none"
          >
            {OVERLAY_SQUAD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-1.5 h-3 w-3 text-muted-foreground" />
        </label>
        <div className="ml-auto flex items-center gap-3">
          <div className="text-xs font-medium tabular-nums text-muted-foreground">
            {safeIndex + 1} / {deck.length}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Exit presentation"
            className="grid h-8 w-8 place-items-center rounded-[10px] border border-soft text-anchor transition-colors ring-focus hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 items-stretch gap-3">
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => setIndex((current) => Math.max(current - 1, 0))}
            disabled={atStart}
            aria-label="Previous slide"
            className={navButtonClass(atStart)}
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        </div>
        <div
          className={cn(
            "grid min-h-0 min-w-0 flex-1 gap-3",
            layout === "full" && "grid-cols-1 grid-rows-1",
            layout === "side-by-side" && "grid-cols-2 grid-rows-1",
            layout === "stacked-left" && "grid-cols-2 grid-rows-2",
          )}
        >
          {slide.map((spec, cardIndex) => (
            <div
              key={spec.id}
              className={cn(
                "min-h-0 min-w-0",
                layout === "stacked-left" && STACKED_LEFT_SLOTS[cardIndex],
              )}
            >
              <EngineeringChart
                spec={spec}
                from={from}
                to={to}
                squadMode={squadMode}
                granularity={granularity}
                presentation
              />
            </div>
          ))}
        </div>
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => setIndex((current) => Math.min(current + 1, deck.length - 1))}
            disabled={atEnd}
            aria-label="Next slide"
            className={navButtonClass(atEnd)}
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
